/**
 * English catalog — Agentic Workflows surfaces (Skills tab + agent-run
 * transcript). Split from en.ts the same way enBoards is, keeping every
 * catalog file under the 500-line fileSize ratchet. Spread into `en` in
 * ./en.ts — key typing (MessageKey) still flows from the merged object.
 */
export const enAgent = {
  // ── OAuth: browser-navigated /oauth/authorize (extension, desktop hosts) ──
  'oauthAuthorize.title': 'Connecting an app to BuzzBit X',
  'oauthAuthorize.working': 'Checking the request and taking you to the consent step…',
  'oauthAuthorize.missingParams': 'This link is missing the OAuth parameters (client_id and redirect_uri). Start the connection again from the app.',
  'oauthAuthorize.failed': 'The authorization request was rejected.',
  'oauthAuthorize.backToApp': 'Back to BuzzBit X',
  // ── Claude settings: Skills tab ───────────────────────────────────────
  'claudeSkills.tabLabel': 'Skills',
  'claudeSkills.intro':
    'Skills are reusable instructions for your Claude — in chat, in Claude Code, and as autonomous agent steps. Standard SKILL.md format: import and export freely.',
  'claudeSkills.empty': 'No skills yet. Create one or import a SKILL.md file.',
  'claudeSkills.loading': 'Loading skills…',
  'claudeSkills.loadFailed': 'Could not load skills.',
  'claudeSkills.versionBadge': 'v{version}',
  'claudeSkills.toolCount': '{count} tools allowed',
  'claudeSkills.noTools': 'No agent tools — cannot run autonomously',
  'claudeSkills.inactive': 'Inactive',
  'claudeSkills.newSkill': 'New skill',
  'claudeSkills.importSkill': 'Import SKILL.md',
  'claudeSkills.importPlaceholderHint': 'Paste the full SKILL.md content (frontmatter + body).',
  'claudeSkills.importAction': 'Import',
  'claudeSkills.export': 'Export',
  'claudeSkills.edit': 'Edit',
  'claudeSkills.delete': 'Delete',
  'claudeSkills.nameLabel': 'Name',
  'claudeSkills.descriptionLabel': 'Description (what this skill is for — drives when Claude uses it)',
  'claudeSkills.contentLabel': 'Instructions (markdown)',
  'claudeSkills.triggersLabel': 'Trigger phrases (comma-separated)',
  'claudeSkills.allowlistLabel': 'Agent tool allowlist (comma-separated tool names)',
  'claudeSkills.allowlistHint':
    'Tools an autonomous agent run may use under this skill. Empty = the skill works in chat but cannot run alone.',
  'claudeSkills.maxTurnsLabel': 'Max agent turns (1–15, blank = default)',
  'claudeSkills.save': 'Save skill',
  'claudeSkills.cancel': 'Cancel',
  'claudeSkills.saveFailed': 'Could not save the skill.',
  'claudeSkills.planRequired': 'Custom skills require the Agency or Enterprise plan.',

  // ── Agent missions (autonomous runs panel in the Lab's Active tab) ─────
  'missions.title': 'Agent missions',
  'missions.subtitle': 'Autonomous Claude runs on a trigger — capped per day, every action logged.',
  'missions.empty': 'No missions yet. Build one on the canvas: a trigger plus a Claude Agent step.',
  'missions.loadFailed': 'Could not load missions.',
  'missions.dailyCap': '{cap}/day cap',
  'missions.trigger.SCHEDULE': 'Scheduled',
  'missions.trigger.EVENT': 'On event',
  'missions.trigger.WEBHOOK': 'Webhook',
  'missions.trigger.MANUAL': 'Manual',
  'missions.approvalRequired': 'Risky actions need approval',
  'missions.autoExecute': 'Runs without approval',
  'missions.runNow': 'Run now',
  'missions.pause': 'Pause',
  'missions.resume': 'Resume',
  'missions.paused': 'Paused',
  'missions.active': 'Active',
  'missions.ranNow': 'Mission started — see the run history for the transcript.',
  'missions.actionFailed': 'That did not work — try again.',

  // ── Agent runs (Claude Agent node transcript) ─────────────────────────
  'agentRun.title': 'Agent transcript',
  'agentRun.loading': 'Loading transcript…',
  'agentRun.loadFailed': 'Could not load the agent transcript.',
  'agentRun.empty': 'No transcript yet.',
  'agentRun.turnsLabel': '{turns} turns · {toolCalls} tool calls',
  'agentRun.waitingApproval': 'The agent wants to run {tool} — approve to continue.',
  'agentRun.approvalTtlNote': 'Auto-rejected after 48h without a decision.',
  'agentRun.approve': 'Approve',
  'agentRun.reject': 'Reject',
  'agentRun.viewTranscript': 'View agent transcript',
  'agentRun.statusLabel': 'Status: {status}',
  'agentRun.summaryLabel': 'Result',
  // ── Agent canvas: per-language message text ───────────────────────────
  'agent.message.languagesTitle': 'Language versions (optional)',
  'agent.message.languagesHint':
    "Customers get the version in the language they write in; everyone else gets the message above.",
  'agent.message.textHe': 'Hebrew version',
  'agent.message.waitHours': 'Wait for a reply (hours)',
  'agent.message.waitHoursHint': 'After this, a tap on a button no longer continues this flow. Default 24 hours.',
  'agent.message.fallbackTitle': 'Outside WhatsApp\'s 24-hour window',
  'agent.message.fallbackHint': 'WhatsApp only allows approved templates 24 hours after the customer\'s last message. Name one to send instead; with none, the message is not sent.',
  'agent.message.fallbackName': 'Approved template name',
  'agent.message.fallbackLanguage': 'Language',
  'agent.publish.needsKeyword': 'Add at least one keyword to the trigger before publishing this flow.',
  'agent.message.textEn': 'English version',
  // ── BuzzBit X for Chrome (the extension bridge in the web chat) ────────
  'extension.connected': 'BuzzBit X for Chrome connected — browser steps run in the side panel and ask you there.',
  'extension.panelClosed': 'BuzzBit X for Chrome is installed. Open its side panel to run browser steps from this chat.',
  'extension.openPanel': 'Open panel',
  'extension.workspaceMismatch': 'BuzzBit X for Chrome is signed into another workspace — switch it to use browser steps here.',
  'extension.signedOut': 'Sign in to BuzzBit X for Chrome to run browser steps from this chat.',
  'extension.toolRunning': 'Browser step: {name}…',
  'extension.toolDone': 'Browser step: {name}',
  'extension.toolFailed': 'Browser step failed: {name}',
  'extension.thumbnailAlt': 'Screenshot from the browser panel',
  // ── Settings → Integrations tile ──────────────────────────────────────
  'extension.tileName': 'BuzzBit X for Chrome',
  'extension.tileDescription':
    'The assistant in a Chrome side panel: chat on any page, research pages into your boards, a private local model over your files, reels.',
  'extension.modalSubtitle': 'Side panel for Chrome',
  'extension.install': 'Install from the Chrome Web Store',
  'extension.installUnpacked':
    'Not on the Web Store yet: unzip the build, open chrome://extensions, turn on Developer mode, choose “Load unpacked” and pick the folder. Then click the BuzzBit X icon or press Ctrl+Shift+B (⌘⇧B on Mac).',
  'extension.installLocalDev':
    'Local setup: in packages/chrome-extension run npm run build:dev, then chrome://extensions → Developer mode → Load unpacked → select the dist folder. Reload the extension after each rebuild. Sign in inside the side panel to this workspace, then press Check again.',
  'extension.statusAbsent': 'Not detected in this browser. Install it, or open this page in Chrome.',
  'extension.statusConnected': 'Installed, signed in to this workspace, panel open.',
  'extension.statusPanelClosed': 'Installed and signed in. Open the side panel to use it here.',
  'extension.statusMismatch': 'Installed, but signed in to a different workspace.',
  'extension.statusSignedOut': 'Installed. Sign in inside the panel to connect it to BuzzBit X.',
  'extension.refresh': 'Check again',
  'extension.refreshDone': 'Status updated: {status}',
  'extension.openPanelFailed': 'Could not open the side panel. {hint}',
  'extension.openPanelOpened': 'Side panel opened.',
  'extension.versionLabel': 'Extension version',
  'extension.disconnect': 'Disconnect',
  'extension.disconnectConfirmTitle': 'Disconnect BuzzBit X for Chrome?',
  'extension.disconnectConfirm':
    'This signs the Chrome side panel out of BuzzBit X on this computer. Browser steps from the web app stop until you sign in again inside the panel.',
  'extension.disconnected': 'Chrome extension signed out.',
  'extension.disconnectFailed': 'Could not disconnect the Chrome extension. Try again.',
  'extension.help':
    'Sign-in happens inside the panel: it opens a BuzzBit X consent page and you approve it for one workspace. Nothing on this page sends tokens to the extension.',
  'extension.localHint':
    'For private file search, run Ollama or LM Studio on your computer and set it in the panel’s Settings — the panel shows the one setting Ollama needs.',

  // ── MCP Activity tab (attribution line) ────────────────────────────────
  'activity.attribution.both': '{actor} · customer {customer}',
  'activity.attribution.actorOnly': '{actor}',
  'activity.attribution.customerOnly': 'Customer {customer}',
} as const;
